<template>
  <div class="fixed top-0 inset-x-0 z-50 w-full pointer-events-none">
    <!-- Desktop Navigation Layer (Removed overflow-hidden to allow dropdown visibility) -->
    <header
      class="hidden lg:flex items-center justify-between mx-auto h-20 px-8 transition-all duration-500 pointer-events-auto relative"
      :class="{
        'h-16 bg-[#120900]/75 border-b border-white/5 backdrop-blur-md':
          isScrolled,
      }"
    >
      <!-- Subtly contained absolute background accents to avoid horizontal viewport scroll -->
      <div
        class="absolute top-0 inset-x-0 h-px bg-linear-to-r from-transparent via-white/10 to-transparent max-w-full"
      />

      <div class="flex-1 flex items-center justify-start">
        <NuxtLink
          :to="status === 'authenticated' ? '/explore' : '/'"
          class="transition-transform duration-300 hover:scale-105 active:scale-95"
        >
          <PebbleAppHomeLogo class="h-8 w-auto" />
        </NuxtLink>
      </div>

      <!-- Desktop Center Navigation Capsule -->
      <nav
        class="flex items-center gap-1 p-1 bg-white/3 border border-white/5 rounded-full backdrop-blur-md"
      >
        <template v-if="status === 'authenticated'">
          <NuxtLink
            v-for="link in navLinks"
            :key="link.name"
            :to="link.href"
            class="relative flex items-center gap-2.5 px-5 py-2 rounded-full text-[13px] font-medium tracking-wide transition-all duration-300 group overflow-hidden nav-link"
            active-class="nav-link-active"
          >
            <component
              :is="link.icon"
              :size="16"
              :stroke-width="2"
              class="z-10 transition-transform group-hover:scale-110 icon-muted group-hover:text-white"
            />
            <span
              class="z-10 tracking-wider text-white/40 group-hover:text-white"
            >
              {{ link.name }}
            </span>
            <div
              class="active-indicator absolute inset-0 bg-white/5 border border-white/10 rounded-full opacity-0 transition-opacity duration-300"
            />
          </NuxtLink>
        </template>

        <template v-else>
          <!-- <NuxtLink
            to="/explore"
            class="relative flex items-center gap-2.5 px-5 py-2 rounded-full text-[13px] font-medium tracking-wide transition-all duration-300 group overflow-hidden nav-link"
            active-class="nav-link-active"
          >
            <component
              :is="Compass"
              :size="16"
              :stroke-width="2"
              class="z-10 transition-transform group-hover:scale-110 icon-muted group-hover:text-white"
            />
            <span class="z-10 tracking-wider text-white/40 group-hover:text-white">
              Explore
            </span>
            <div
              class="active-indicator absolute inset-0 bg-white/5 border border-white/10 rounded-full opacity-0 transition-opacity duration-300"
            />
          </NuxtLink>

          <div class="h-4 w-px bg-white/10 mx-2" /> -->

          <div class="flex items-center gap-0.5 px-1.5">
            <a
              v-for="social in socialLinks"
              :key="social.label"
              :href="social.href"
              target="_blank"
              rel="noopener noreferrer"
              class="size-8 rounded-full flex items-center justify-center text-white/40 hover:text-brand hover:bg-white/5 transition-all duration-300"
              :title="social.label"
            >
              <component :is="social.icon" :size="14" stroke-width="2" />
            </a>
          </div>
        </template>
      </nav>

      <!-- Desktop Right System Actions Block -->
      <div class="flex-1 flex justify-end items-center gap-4">
        <template v-if="status !== 'authenticated'">
          <div
            class="flex items-center gap-1 p-1 bg-white/3 border border-white/5 rounded-full backdrop-blur-md"
          >
            <NuxtLink
              to="/login"
              class="relative flex items-center px-4 py-2 rounded-full text-[13px] font-medium tracking-wide text-white/50 hover:text-white transition-colors duration-300"
            >
              Sign In
            </NuxtLink>

            <PebbleAppLink
              to="/get-started-as"
              variant="primary"
              size="sm"
              class="text-xs font-bold tracking-wider rounded-full py-1.5! px-4!"
            >
              Get Started
            </PebbleAppLink>
          </div>
        </template>

        <template v-else>
          <PebbleAppLink
            v-if="isAnyCreator"
            type="button"
            variant="outline"
            :leading-icon="SquarePlus"
            :to="getContentCreatorDashboardUrl"
            class="text-white border-white/20 hover:bg-white/10 text-xs font-bold tracking-wider rounded-full py-1.5! px-4!"
          >
            Create
          </PebbleAppLink>

          <!-- Isolated relative positioning layout zone -->
          <div class="relative z-50">
            <PebbleAppUserDropdown />
          </div>
        </template>
      </div>
    </header>

    <!-- Mobile Navigation Layer -->
    <header
      class="lg:hidden w-full h-20 flex items-center justify-between px-6 transition-all duration-500 pointer-events-auto"
      :class="{
        'h-16 bg-[#0f0f11]/80 backdrop-blur-md border-b border-white/5':
          isScrolled,
      }"
    >
      <div class="flex-1 flex items-center">
        <NuxtLink
          :to="status === 'authenticated' ? '/explore' : '/'"
          class="transition-transform duration-300 hover:scale-105 active:scale-95"
        >
          <PebbleAppHomeLogo class="h-6 w-auto" />
        </NuxtLink>
      </div>

      <!-- Center Links Container for Mobile View -->
      <nav
        class="flex items-center gap-0.5 p-0.5 bg-white/3 border border-white/5 rounded-full backdrop-blur-md mx-2"
      >
        <template v-if="status === 'authenticated'">
          <NuxtLink
            v-for="link in navLinks"
            :key="'mob-' + link.name"
            :to="link.href"
            class="relative flex items-center px-3.5 py-1.5 rounded-full text-[11px] font-semibold tracking-wide transition-all duration-300"
            active-class="nav-link-active bg-white/5 border border-white/10"
          >
            <span class="tracking-wider text-white/40">
              {{ link.name }}
            </span>
          </NuxtLink>
        </template>
        <!-- <template v-else>
          <NuxtLink
            to="/explore"
            class="relative flex items-center px-4 py-1.5 rounded-full text-[11px] font-semibold tracking-wide transition-all duration-300"
            active-class="nav-link-active bg-white/5 border border-white/10"
          >
            <span class="tracking-wider text-white/40">Explore</span>
          </NuxtLink>
        </template> -->
      </nav>

      <!-- Right System Menu Target for Mobile View -->
      <div class="flex-1 flex justify-end items-center gap-3">
        <template v-if="status !== 'authenticated'">
          <NuxtLink
            to="/login"
            class="text-[12px] font-semibold tracking-wide text-white/60 hover:text-white transition-colors px-2"
          >
            Sign In
          </NuxtLink>
        </template>

        <template v-else>
          <PebbleAppLink
            v-if="isAnyCreator"
            type="button"
            variant="outline"
            :to="getContentCreatorDashboardUrl"
            class="text-white border-white/10 hover:bg-white/5 text-[10px] font-black tracking-wider rounded-full px-2.5! py-1!"
          >
            <component :is="SquarePlus" :size="14" />
          </PebbleAppLink>

          <div class="relative z-50">
            <PebbleAppUserDropdown />
          </div>
        </template>
      </div>
    </header>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useRuntimeConfig } from "#app";
import useGetUserRole from "~/utils/auth/useGetUserRole";
import {
  Compass,
  Film,
  Tv,
  Mic2,
  Video,
  Navigation,
  SquarePlus,
  Twitter,
  Instagram,
  Youtube,
  Clapperboard,
  Facebook,
} from "lucide-vue-next";

const config = useRuntimeConfig();
const landingPageUrl = config.public.landingPageUrl;

const { status, user } = useAuth();
const { getContentCreatorDashboardUrl } = useGetPebbleCmsUrls();

const isScrolled = ref(false);

const navLinks = [
  { name: "Explore", icon: Navigation, href: landingPageUrl },
  // { name: "Premieres", icon: Video, href: "/content/premieres" },
  // { name: "Movies", icon: Film, href: "/content/movies" },
  // { name: "TV Shows", icon: Tv, href: "/content/tv-shows" },
  // { name: "Podcasts", icon: Mic2, href: "/content/podcasts" },
];

const socialLinks = [
  {
    label: "YouTube",
    icon: Youtube,
    href: "https://youtube.com/@trypebblenow?si=SB-BZUx3FvBZyVH1",
  },
  {
    label: "X",
    icon: Twitter,
    href: "https://x.com/trypebble?s=21&t=v53AHsB2zFSwKBeN-AU63g",
  },
  {
    label: "Instagram",
    icon: Instagram,
    href: "https://www.instagram.com/trypebblenow?igsh=eWdtZTU2NmR2enJ1",
  },
  {
    label: "TikTok",
    icon: Clapperboard,
    href: "https://www.tiktok.com/@trypebblenow?_r=1&_t=ZS-97G9k2Nh7bU",
  },
  {
    label: "Facebook",
    icon: Facebook,
    href: "https://www.facebook.com/share/1BLSQKD2F7/?mibextid=wwXIfr",
  },
];

const handleScroll = () => {
  isScrolled.value = window.scrollY > 20;
};

const isAnyCreator = computed(() => {
  if (status.value !== "authenticated" || !user.value) return false;
  return useGetUserRole(user.value).isAnyCreator();
});

onMounted(() => {
  window.addEventListener("scroll", handleScroll, { passive: true });
});

onUnmounted(() => {
  window.removeEventListener("scroll", handleScroll);
});
</script>

<style scoped>
.icon-muted {
  color: rgba(255, 255, 255, 0.4);
  transition:
    color 0.3s ease,
    transform 0.3s ease;
}

.nav-link span {
  color: rgba(255, 255, 255, 0.4);
  transition: color 0.3s ease;
}

.nav-link-active svg,
.nav-link-active span {
  color: #ffffff !important;
  opacity: 1 !important;
}

.nav-link-active .active-indicator {
  opacity: 1;
}
</style>
